import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";

const ReferentiesPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const testimonials = [
    {
      quote: [
        "Rabia heeft bij Van Doorne een wezenlijk verschil gemaakt in ons service-to-cash proces. Ze bracht op een doortastende en methodische manier overzicht, orde en rust — zowel in de processen als in het team.",
        "Wat mij in het bijzonder is bijgebleven, is hoe zij het credit- en billingteam wist te stabiliseren en te versterken. Dat vereist niet alleen inhoudelijke kennis, maar ook het vermogen om mensen mee te nemen, vertrouwen op te bouwen en bruggen te slaan tussen disciplines die niet altijd vanzelfsprekend samenwerken. Rabia deed dat met overtuiging.",
        "Ze heeft bovendien een significante bijdrage geleverd aan de samenwerking tussen de advocatuur, het secretariaat en het finance team.",
        "Rabia is iemand die resultaten boekt, maar ook oog heeft voor de menselijke kant van verandering."
      ],
      name: "Anouk Rutten",
      title: "Board Member | COO",
      company: "Van Doorne"
    },
    {
      quote: [
        "Rabia is voor mij bepalend geweest in de ontwerp-, bouw- en implementatiefase van een nieuw financieel systeem voor debiteurenbeheer. Ook in de aftercare fase speelde zij een belangrijke rol.",
        "Daarbij heeft zij niet alleen oog voor de credit management processen, maar ook voor aanpalende processen zoals accounting, controlling en de koppeling met de bronadministratie. Ketendenken is haar kracht.",
        "De samenwerking met Rabia heb ik op persoonlijk en zakelijk vlak als zeer prettig ervaren."
      ],
      name: "Edwin Oversteeg",
      title: "Business Change Manager Service Delivery",
      company: "Achmea"
    }
  ];

  return (
    <div data-testid="referenties-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-mist">
        <div className="container-custom">
          <div className="max-w-3xl">
            <motion.span {...fadeUp} className="caption text-cta mb-4 block">
              Referenties
            </motion.span>
            <motion.h1 
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="heading-1 mb-6"
              data-testid="referenties-headline"
            >
              Wat klanten zeggen
            </motion.h1>
            <motion.p 
              {...fadeUp}
              transition={{ delay: 0.2 }}
              className="body-large"
            >
              Ervaring uit eerste hand van organisaties die met Regie op Cashflow 
              hebben samengewerkt aan hun financiële transformatie.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Full Testimonials */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="space-y-16 max-w-4xl mx-auto">
            {testimonials.map((testimonial, idx) => (
              <motion.article
                key={testimonial.name}
                {...fadeUp}
                transition={{ delay: 0.1 * idx }}
                className="relative"
                data-testid={`full-testimonial-${idx}`}
              >
                <div className="bg-mist p-8 md:p-12 lg:p-16 relative">
                  <Quote className="w-16 h-16 text-cta/20 absolute top-8 right-8 hidden md:block" />
                  
                  <div className="space-y-4 mb-8">
                    {testimonial.quote.map((paragraph, pIdx) => (
                      <p key={pIdx} className="text-navy text-lg leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  <div className="border-t border-sand pt-6 flex items-center gap-4">
                    <div className="w-1 h-12 bg-cta rounded-full" />
                    <div>
                      <span className="font-serif text-xl text-navy block">{testimonial.name}</span>
                      <span className="text-navy/70">{testimonial.title}</span>
                      <span className="text-cta font-medium block">{testimonial.company}</span>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-burgundy text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span {...fadeUp} className="text-cta text-xs tracking-widest uppercase mb-4 block">
              Volgende Stap
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-white mb-6">
              Klaar om jouw cashflow te verbeteren?
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-white/70 text-lg mb-10">
              In een gesprek bekijken we samen waar in jouw organisatie de 
              grootste kansen liggen. Geen verplichtingen, wel concrete inzichten.
            </motion.p>
            <motion.div {...fadeUp} transition={{ delay: 0.3 }}>
              <a
                href="https://calendly.com/rabia-regieopcashflow/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="referenties-cta"
                className="bg-white text-burgundy px-8 py-4 rounded-sm hover:bg-cta hover:text-white transition-all duration-300 text-sm tracking-widest uppercase font-semibold inline-flex items-center gap-2"
              >
                Plan een gesprek
                <ArrowRight size={18} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReferentiesPage;
